import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

/**
 * Hook para buscar as participações do usuário em sessões públicas
 * @param userId - ID do usuário logado
 * @returns Tupla com [participações, função para adicionar participação]
 */
export const useUserParticipations = (
  userId: string | undefined
): [Set<string>, (appointmentId: string) => void] => {
  const [userParticipations, setUserParticipations] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) {
      setUserParticipations(new Set());
      return;
    }

    const fetchUserParticipations = async () => {
      try {
        // Busca todos os appointments que permitem participantes
        const appointmentsSnapshot = await getDocs(
          query(collection(db, "appointments"), where("isPublic", "==", true))
        );

        // Verifica em quais appointments o usuário está participando
        const participationChecks = await Promise.all(
          appointmentsSnapshot.docs.map(async (appointmentDoc) => {
            const participantDoc = await getDocs(
              query(
                collection(db, "appointments", appointmentDoc.id, "participants"),
                where("userId", "==", userId)
              )
            );
            return {
              appointmentId: appointmentDoc.id,
              isParticipant: !participantDoc.empty,
            };
          })
        );

        // Cria um Set com os IDs dos appointments onde o usuário participa
        const participations = new Set(
          participationChecks.filter((p) => p.isParticipant).map((p) => p.appointmentId)
        );

        setUserParticipations(participations);
      } catch (error) {
        console.error("Erro ao buscar participações do usuário:", error);
        setUserParticipations(new Set());
      }
    };

    fetchUserParticipations();
  }, [userId]);

  // Função para adicionar uma nova participação localmente
  const addParticipation = (appointmentId: string) => {
    setUserParticipations((prev) => new Set(prev).add(appointmentId));
  };

  return [userParticipations, addParticipation];
};
